import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Table, Spinner, Form, Row, Col } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";
import { fetchDrawOptions } from "../../utils/draws";

const todayISO = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Port-au-Prince",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const emptyForm = () => ({
  id: null,
  date: todayISO(),
  draw: "",
  win_1: "",
  win_2: "",
  win_3: "",
});

const fieldStyle = (ok) => ({
  borderRadius: "4px",
  border: `1px solid ${ok ? "#28A745" : "#DC3545"}`,
  paddingRight: "2.2rem",
});

const FieldIcon = ({ ok }) => (
  <FontAwesomeIcon
    icon={ok ? faCheck : faTimes}
    className={ok ? "text-success" : "text-danger"}
    style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
  />
);

export default function LotsGagnant() {
  const [viewMode, setViewMode] = useState("lister"); // ajouter | lister
  const [draws, setDraws] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(emptyForm());

  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [appliedRange, setAppliedRange] = useState({ start: todayISO(), end: todayISO() });

  const [drawsLoading, setDrawsLoading] = useState(false);
  const [drawsError, setDrawsError] = useState("");

  const loadDraws = useCallback(async () => {
    setDrawsLoading(true);
    setDrawsError("");
    try {
      const list = await fetchDrawOptions();
      setDraws(list);
      if (list.length === 0) {
        setDrawsError("Impossible de charger les tirages. Vérifiez que le backend tourne (port 8000) et que vous êtes connecté.");
      }
    } catch (e) {
      console.error("loadDraws error:", e);
      setDraws([]);
      setDrawsError("Erreur réseau lors du chargement des tirages.");
    } finally {
      setDrawsLoading(false);
    }
  }, []);

  const fetchDraws = loadDraws;

  const fetchResults = useCallback(async (start, end) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        start_date: start,
        end_date: end,
        page: "1",
        page_size: "200",
      });
      const data = await client(`results/?${qs.toString()}`);
      const list = data?.results || (Array.isArray(data) ? data : []);
      setResults(list);
    } catch (e) {
      console.error("results error:", e);
      setResults([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDraws();
    fetchResults(appliedRange.start, appliedRange.end);
  }, [loadDraws, fetchResults, appliedRange]);

  const handleValidate = () => {
    if (!startDate || !endDate) {
      alert("Veuillez choisir Debut et Fin.");
      return;
    }
    if (startDate > endDate) {
      alert("La date Debut doit être avant la date Fin.");
      return;
    }
    setAppliedRange({ start: startDate, end: endDate });
  };

  const setLotField = (name, value) => {
    const cleaned = String(value).replace(/\D/g, "");
    setForm((prev) => ({ ...prev, [name]: cleaned }));
  };

  const openCreate = () => {
    setForm(emptyForm());
    setFormError("");
    fetchDraws();
    setViewMode("ajouter");
  };

  const openEdit = (row) => {
    if (row.is_global) {
      alert("Ce lot est global (plateforme). Modifiez-le uniquement depuis l'admin Django.");
      return;
    }
    const w1 = row.win_1 || "";
    setForm({
      id: row.id,
      date: row.date || todayISO(),
      draw: row.draw != null ? String(row.draw) : "",
      win_1: w1,
      win_2: row.win_2 || "",
      win_3: row.win_3 || "",
    });
    setFormError("");
    loadDraws();
    setViewMode("ajouter");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError("");
    const win1 = (form.win_1 || "").trim();
    if (!form.date || !form.draw) {
      setFormError("Veuillez choisir la date et le tirage.");
      return;
    }
    if (win1.length !== 3 || form.win_2.length !== 2 || form.win_3.length !== 2) {
      setFormError("Le 1er lot doit contenir 3 chiffres; les 2em et 3em lots doivent contenir 2 chiffres.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        draw: Number(form.draw),
        win_1: win1,
        win_2: form.win_2,
        win_3: form.win_3,
      };
      if (form.id) {
        await client(`results/${form.id}/`, { method: "PUT", body: payload });
      } else {
        await client("results/", { method: "POST", body: payload });
      }
      setForm(emptyForm());
      setViewMode("lister");
      await fetchResults(appliedRange.start, appliedRange.end);
    } catch (err) {
      const msg =
        err?.error ||
        err?.detail ||
        (typeof err === "object" ? JSON.stringify(err) : null) ||
        "Erreur lors de l'enregistrement.";
      setFormError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const okDate = !!form.date;
  const okDraw = !!form.draw;
  const okWin1 = (form.win_1 || "").length === 3;
  const okWin2 = (form.win_2 || "").length === 2;
  const okWin3 = (form.win_3 || "").length === 2;

  return (
    <div style={{ padding: "16px 0" }}>
      <div className="d-flex mb-4 flex-wrap" style={{ gap: "10px" }}>
        <Button
          variant={viewMode === "ajouter" ? "primary" : "outline-primary"}
          style={{ borderRadius: "4px", fontSize: "0.9rem", padding: "6px 20px", fontWeight: "500", color: viewMode === "ajouter" ? "#FFF" : undefined }}
          onClick={openCreate}
        >
          Ajouter
        </Button>
        <Button
          variant={viewMode === "lister" ? "success" : "outline-success"}
          style={{ borderRadius: "4px", fontSize: "0.9rem", padding: "6px 20px", fontWeight: "500", color: viewMode === "lister" ? "#FFF" : undefined }}
          onClick={() => setViewMode("lister")}
        >
          Lister
        </Button>
      </div>

      {viewMode === "ajouter" ? (
        <Card className="border-0 shadow-sm" style={{ borderRadius: "8px" }}>
          <Card.Body className="p-4">
            <h5 className="mb-4" style={{ fontWeight: "bold", color: "#333" }}>
              {form.id ? "Modifier lot gagnant" : "Ajouter lot gagnant"}
            </h5>

            <Form onSubmit={handleSave}>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Form.Label className="fw-bold small mb-1">Date</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      style={fieldStyle(okDate)}
                    />
                    <FieldIcon ok={okDate} />
                  </div>
                </Col>
                <Col md={6}>
                  <Form.Label className="fw-bold small mb-1 d-flex justify-content-between align-items-center">
                    <span>
                      Tirage {drawsLoading ? "(chargement…)" : draws.length > 0 ? `(${draws.length})` : ""}
                    </span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={loadDraws}
                      disabled={drawsLoading}
                    >
                      Actualiser
                    </Button>
                  </Form.Label>
                  <Form.Select
                    value={form.draw}
                    onChange={(e) => setForm({ ...form, draw: e.target.value })}
                    style={{
                      borderRadius: "4px",
                      border: `1px solid ${okDraw ? "#28A745" : "#DC3545"}`,
                      height: "38px",
                    }}
                    disabled={drawsLoading}
                  >
                    <option value="">Choisir...</option>
                    {draws.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </Form.Select>
                  {drawsError && (
                    <div className="text-danger small mt-1">{drawsError}</div>
                  )}
                </Col>
              </Row>

              <Row className="g-3 mb-4">
                <Col md={4}>
                  <Form.Label className="fw-bold small mb-1">1er lot</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      value={form.win_1}
                      onChange={(e) => setLotField("win_1", e.target.value)}
                      style={fieldStyle(okWin1)}
                    />
                    <FieldIcon ok={okWin1} />
                  </div>
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-bold small mb-1">2em lot</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={form.win_2}
                      onChange={(e) => setLotField("win_2", e.target.value)}
                      style={fieldStyle(okWin2)}
                    />
                    <FieldIcon ok={okWin2} />
                  </div>
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-bold small mb-1">3em lot</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={form.win_3}
                      onChange={(e) => setLotField("win_3", e.target.value)}
                      style={fieldStyle(okWin3)}
                    />
                    <FieldIcon ok={okWin3} />
                  </div>
                </Col>
              </Row>

              {formError && (
                <div className="alert alert-danger py-2 small mb-3">{formError}</div>
              )}

              <Button
                type="submit"
                disabled={saving}
                className="w-100"
                style={{ backgroundColor: "#007BFF", borderColor: "#007BFF", borderRadius: "4px", fontWeight: "600", padding: "10px", fontSize: "1rem" }}
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm" style={{ borderRadius: "8px" }}>
          <Card.Body className="p-4">
            <h5 className="mb-3" style={{ fontWeight: "bold", color: "#333" }}>
              Liste des lots gagnant
            </h5>

            <Row className="g-3 mb-3 align-items-end">
              <Col md={6}>
                <Form.Label className="fw-bold small mb-1">Debut</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={fieldStyle(!!startDate)}
                  />
                  <FieldIcon ok={!!startDate} />
                </div>
              </Col>
              <Col md={6}>
                <Form.Label className="fw-bold small mb-1">Fin</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={fieldStyle(!!endDate)}
                  />
                  <FieldIcon ok={!!endDate} />
                </div>
              </Col>
            </Row>

            <Button
              className="w-100 mb-3"
              onClick={handleValidate}
              style={{ backgroundColor: "#007BFF", borderColor: "#007BFF", borderRadius: "4px", fontWeight: "600", padding: "10px" }}
            >
              Valider
            </Button>

            <div className="mb-3 fw-bold" style={{ fontSize: "0.95rem", color: "#222" }}>
              <div>Debut : {appliedRange.start}</div>
              <div>Fin : {appliedRange.end}</div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <div className="table-responsive">
                <Table bordered hover className="align-middle mb-0" style={{ borderColor: "#DDD" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F8F9FA" }}>
                      <th style={{ width: "100px" }} />
                      <th style={{ fontSize: "0.8rem", fontWeight: "bold" }}>Date</th>
                      <th style={{ fontSize: "0.8rem", fontWeight: "bold" }}>Tirage</th>
                      <th style={{ fontSize: "0.8rem", fontWeight: "bold" }}>1er lot</th>
                      <th style={{ fontSize: "0.8rem", fontWeight: "bold" }}>2em lot</th>
                      <th style={{ fontSize: "0.8rem", fontWeight: "bold" }}>3em lot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          Aucun lot gagnant pour cette période.
                        </td>
                      </tr>
                    ) : (
                      results.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <Button
                              size="sm"
                              style={{ backgroundColor: "#007BFF", borderColor: "#007BFF", color: "#FFF", borderRadius: "2px", fontSize: "0.8rem", fontWeight: "500", padding: "4px 12px" }}
                              onClick={() => openEdit(r)}
                            >
                              Modifier
                            </Button>
                          </td>
                          <td style={{ fontSize: "0.88rem" }}>{r.date}</td>
                          <td style={{ fontSize: "0.88rem" }}>{r.draw_name}</td>
                          <td style={{ fontSize: "0.88rem" }}>{r.win_1}</td>
                          <td style={{ fontSize: "0.88rem" }}>{r.win_2}</td>
                          <td style={{ fontSize: "0.88rem" }}>{r.win_3}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
